// ============================================================================
// DynamicGrid — repeating sub-form (array of objects in JSONB)
// ============================================================================
// Renders a table-style grid where each row is a mini form following
// `field.row_fields`. Add / remove / inline-edit. No computed totals,
// no cross-row formulas — kept intentionally simple for the demo.
//
// Stored in `data` JSONB at the parent state's row (under `field.key`).

import React, { useCallback, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import type {
  FormValues,
  GridFieldSpec,
  GridRowFieldSpec,
  GridRowValues,
} from 'api/types/formSpec.types';
import DynamicField from './DynamicField';
import { runRowValidations } from './validationEngine';
import { evalBool } from './expressionEvaluator';
import { hasSeedResolver, resolveSeed, type SeedContext } from './seedResolver';

export interface DynamicGridProps {
  field: GridFieldSpec;
  /** Array of row objects. */
  value: GridRowValues[] | null | undefined;
  onChange: (next: GridRowValues[]) => void;
  /** Grid-level error message (e.g., "min 1 row required"). */
  error?: string;
  disabled?: boolean;
  /** Opaque context forwarded to seed resolvers (e.g. `{ caseId }`). */
  seedContext?: SeedContext;
}

const DynamicGrid: React.FC<DynamicGridProps> = ({ field, value, onChange, error, disabled, seedContext }) => {
  const rows: GridRowValues[] = useMemo(() => Array.isArray(value) ? value : [], [value]);
  const [editingIdx, setEditingIdx] = useState<number | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});
  const [draft, setDraft] = useState<GridRowValues>({});

  const atMax = field.max_rows != null && rows.length >= field.max_rows;
  const atMin = field.min_rows != null && rows.length <= field.min_rows;
  const [isSeeding, setIsSeeding] = useState(false);

  const canSeed = !!field.seed_from && hasSeedResolver(field.seed_from);

  const handlePrefill = useCallback(async () => {
    if (!field.seed_from || disabled || isSeeding) return;
    setIsSeeding(true);
    try {
      const seeded = await resolveSeed(field.seed_from, seedContext ?? {});
      const dedupeKey = field.seed_dedupe_key;
      let next: GridRowValues[];
      if (dedupeKey) {
        const existing = new Set(rows.map((r) => r[dedupeKey]).filter((v) => v != null));
        const newOnes = seeded.filter((r) => !existing.has(r[dedupeKey]));
        next = [...rows, ...newOnes];
      } else {
        next = rows.length === 0 ? seeded : [...rows, ...seeded];
      }
      onChange(next);
    } finally {
      setIsSeeding(false);
    }
  }, [field.seed_from, field.seed_dedupe_key, disabled, isSeeding, rows, seedContext, onChange]);

  const handleAddClick = useCallback(() => {
    if (atMax || disabled) return;
    setDraft({});
    setRowErrors({});
    setEditingIdx(rows.length); // index = rows.length means "new row"
  }, [atMax, disabled, rows.length]);

  const handleEditClick = useCallback((idx: number) => {
    if (disabled) return;
    setDraft({ ...rows[idx] });
    setRowErrors({});
    setEditingIdx(idx);
  }, [disabled, rows]);

  const handleRemoveClick = useCallback((idx: number) => {
    if (disabled || atMin) return;
    const next = rows.filter((_, i) => i !== idx);
    onChange(next);
  }, [disabled, atMin, rows, onChange]);

  const handleCancelEdit = useCallback(() => {
    setEditingIdx(null);
    setRowErrors({});
    setDraft({});
  }, []);

  const handleSaveRow = useCallback(() => {
    const result = runRowValidations(field.row_fields as any, draft as FormValues);
    if (!result.canSave) {
      setRowErrors(result.fieldErrors);
      return;
    }
    let next: GridRowValues[];
    if (editingIdx === rows.length) {
      next = [...rows, draft];
    } else if (editingIdx != null && editingIdx >= 0) {
      next = rows.map((r, i) => i === editingIdx ? draft : r);
    } else {
      return;
    }
    onChange(next);
    setEditingIdx(null);
    setRowErrors({});
    setDraft({});
  }, [draft, editingIdx, rows, onChange, field.row_fields]);

  const updateDraft = useCallback((key: string, val: unknown) => {
    setDraft((prev) => ({ ...prev, [key]: val }));
    if (rowErrors[key]) setRowErrors((p) => { const n = { ...p }; delete n[key]; return n; });
  }, [rowErrors]);

  const isInlineEdit = field.inline_edit === true;

  // Inline-edit handlers — Add appends a blank row; cells edit in place.
  const handleAddRowInline = useCallback(() => {
    if (atMax || disabled) return;
    onChange([...rows, {}]);
  }, [atMax, disabled, rows, onChange]);

  const updateCell = useCallback((idx: number, key: string, val: unknown) => {
    const next = rows.map((r, i) => i === idx ? { ...r, [key]: val } : r);
    onChange(next);
  }, [rows, onChange]);

  const stepperKeys = field.row_stepper ?? [];
  const stepperLabels = useMemo(
    () => stepperKeys.map((k) => {
      const f = (field.row_fields ?? []).find((rf) => rf.key === k);
      return f?.label ?? k;
    }),
    [stepperKeys, field.row_fields],
  );

  const headerCells = useMemo(() => {
    return (field.row_fields ?? []).map((f) => (
      <th key={f.key} style={cellHeaderStyle(f)}>
        {f.label}{f.required && <span className="text-danger"> *</span>}
      </th>
    ));
  }, [field.row_fields]);

  return (
    <div className="form-group" data-field={field.key}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: '0.4rem' }}>
        <label className="form-label" style={{ margin: 0 }}>
          {field.label}
          {field.required && <span className="text-danger"> *</span>}
          {rows.length > 0 && (
            <span style={{ color: 'var(--text-secondary)', fontWeight: 400, marginLeft: '0.4rem' }}>
              ({rows.length}{field.max_rows ? ` / ${field.max_rows}` : ''})
            </span>
          )}
        </label>
        {!disabled && editingIdx == null && (
          <div style={{ display: 'flex', gap: '0.4rem' }}>
            {canSeed && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '0.25rem 0.65rem', fontSize: '0.78rem' }}
                onClick={() => void handlePrefill()}
                disabled={isSeeding}
                title="Populate rows from this case's parties (Applicant / Co-Applicants / Guarantors)"
              >
                <IconSystem.refresh size={12} style={{ marginRight: '0.3rem' }} />
                {isSeeding ? 'Loading…' : (field.seed_label ?? 'Pre-fill from account')}
              </button>
            )}
            {!atMax && (
              <button
                type="button"
                className="btn btn-secondary"
                style={{ padding: '0.25rem 0.65rem', fontSize: '0.78rem' }}
                onClick={isInlineEdit ? handleAddRowInline : handleAddClick}
              >
                <IconSystem.add size={12} style={{ marginRight: '0.3rem' }} />
                {field.add_label ?? 'Add Row'}
              </button>
            )}
          </div>
        )}
      </div>

      {field.description && (
        <div style={{ fontSize: '0.72rem', color: 'var(--text-secondary)', marginBottom: '0.5rem' }}>
          {field.description}
        </div>
      )}

      {rows.length === 0 && editingIdx == null && (
        <div style={emptyHintStyle}>{field.empty_message ?? 'No rows yet'}</div>
      )}

      {rows.length > 0 && (
        <div style={{ overflowX: 'auto', border: '1px solid var(--form-border-color)', borderRadius: 4 }}>
          <table style={tableStyle}>
            <thead>
              <tr>
                {stepperKeys.length > 0 && (
                  <th style={{ ...cellHeaderStyle(), width: 90 }}>Progress</th>
                )}
                {headerCells}
                <th style={{ ...cellHeaderStyle(), width: 80 }}></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr key={idx}>
                  {stepperKeys.length > 0 && (
                    <td style={cellStyle}>
                      <RowStepper keys={stepperKeys} labels={stepperLabels} row={row} />
                    </td>
                  )}
                  {(field.row_fields ?? []).map((f) => (
                    <td key={f.key} style={isInlineEdit ? inlineCellStyle : cellStyle}>
                      {isInlineEdit ? (
                        (f.show_if && !evalBool(f.show_if, row, true))
                          ? null
                          : (
                            <DynamicField
                              field={f as any}
                              value={row[f.key]}
                              onChange={(v) => updateCell(idx, f.key, v)}
                              disabled={disabled}
                              hideLabel
                            />
                          )
                      ) : (
                        formatRowValue(row[f.key], f)
                      )}
                    </td>
                  ))}
                  <td style={cellStyle}>
                    {!disabled && (
                      <div style={{ display: 'flex', gap: '0.35rem' }}>
                        {!isInlineEdit && (
                          <button
                            type="button"
                            onClick={() => handleEditClick(idx)}
                            style={iconButtonStyle}
                            title="Edit row"
                          ><IconSystem.edit size={12} /></button>
                        )}
                        <button
                          type="button"
                          onClick={() => handleRemoveClick(idx)}
                          style={{ ...iconButtonStyle, color: '#dc2626' }}
                          disabled={atMin}
                          title={atMin ? `At least ${field.min_rows} row(s) required` : 'Remove row'}
                        ><IconSystem.trash size={12} /></button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Popout row editor (suppressed in inline_edit mode) */}
      {!isInlineEdit && editingIdx != null && (
        <div style={rowEditorStyle}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: '0.5rem' }}>
            {editingIdx === rows.length ? 'Add' : `Edit row ${editingIdx + 1}`}
          </div>
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '0.6rem',
          }}>
            {(field.row_fields ?? []).map((f) => {
              if (f.show_if && !evalBool(f.show_if, draft as FormValues, true)) return null;
              return (
                <DynamicField
                  key={f.key}
                  field={f as any}
                  value={draft[f.key]}
                  onChange={(v) => updateDraft(f.key, v)}
                  error={rowErrors[f.key]}
                />
              );
            })}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end', marginTop: '0.5rem' }}>
            <button type="button" className="btn btn-secondary" onClick={handleCancelEdit}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary" onClick={handleSaveRow}>
              {editingIdx === rows.length ? 'Add Row' : 'Update Row'}
            </button>
          </div>
        </div>
      )}

      {error && <div className="form-error-message">{error}</div>}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Helpers
// ----------------------------------------------------------------------------

const cellHeaderStyle = (f?: GridRowFieldSpec): React.CSSProperties => ({
  padding: '0.4rem 0.6rem',
  textAlign: 'left',
  fontSize: '0.75rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  background: 'var(--bg-secondary)',
  borderBottom: '1px solid var(--form-border-color)',
  width: f?.width,
});

const cellStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem',
  fontSize: '0.82rem',
  borderBottom: '1px solid var(--border-color)',
};

// Tighter cell padding for inline-edit mode so inputs sit snugly.
const inlineCellStyle: React.CSSProperties = {
  padding: '0.3rem 0.4rem',
  fontSize: '0.82rem',
  borderBottom: '1px solid var(--border-color)',
  verticalAlign: 'top',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const iconButtonStyle: React.CSSProperties = {
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  padding: '0.25rem',
  color: 'var(--text-secondary)',
  display: 'inline-flex',
  alignItems: 'center',
};

const rowEditorStyle: React.CSSProperties = {
  marginTop: '0.5rem',
  padding: '0.75rem',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--form-border-color)',
  borderRadius: 4,
};

const emptyHintStyle: React.CSSProperties = {
  padding: '0.75rem',
  textAlign: 'center',
  color: 'var(--text-secondary)',
  fontSize: '0.8rem',
  fontStyle: 'italic',
  background: 'var(--bg-secondary)',
  border: '1px dashed var(--form-border-color)',
  borderRadius: 4,
};

// Visual mini-stepper: one dot per row_stepper key, filled green when the
// corresponding cell on this row is populated. Hover tooltip = step label.
const RowStepper: React.FC<{
  keys: string[];
  labels: string[];
  row: GridRowValues;
}> = ({ keys, labels, row }) => (
  <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.2rem' }}>
    {keys.map((k, i) => {
      const v = row[k];
      const filled = v != null && v !== '';
      return (
        <React.Fragment key={k}>
          {i > 0 && (
            <span style={{
              width: 10, height: 1, background: 'var(--text-secondary)', opacity: 0.4,
            }} />
          )}
          <span
            title={`${i + 1}. ${labels[i]}${filled ? ' ✓' : ' (pending)'}`}
            style={{
              display: 'inline-block', width: 9, height: 9, borderRadius: '50%',
              background: filled ? '#16a34a' : 'transparent',
              border: filled ? 'none' : '1.5px solid var(--text-secondary)',
              opacity: filled ? 1 : 0.6,
            }}
          />
        </React.Fragment>
      );
    })}
  </div>
);

function formatRowValue(v: unknown, field: GridRowFieldSpec): React.ReactNode {
  if (v == null || v === '') return <span style={{ color: 'var(--text-secondary)' }}>—</span>;
  if (field.type === 'boolean') return v === true ? '✓' : '✗';
  if (field.type === 'currency') return `₹${Number(v).toLocaleString('en-IN')}`;
  if (field.type === 'select') {
    const opt = (field.options ?? []).find((o) => (typeof o === 'string' ? o : o.value) === v);
    return opt ? (typeof opt === 'string' ? opt : opt.label) : String(v);
  }
  return String(v);
}

export default DynamicGrid;
